'use client';

import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/nextjs';
import { trpc } from '@/lib/trpc/client';
import { getVisitorIdFromCookie, clearVisitorId } from '@/lib/visitor-id';

/**
 * WishlistMigrationHandler automatically migrates anonymous wishlist items
 * to the user's account when they sign in.
 *
 * This component is invisible and should be placed in the main layout.
 * It runs once per session after the user signs in.
 */
export default function WishlistMigrationHandler() {
  const { isSignedIn, isLoaded } = useUser();
  const hasMigrated = useRef(false);
  const utils = trpc.useUtils();

  const migration = trpc.wishlist.migrateVisitorToUser.useMutation({
    onSuccess: (data) => {
      if (data.migratedCount > 0) {
        console.log(`[WishlistMigration] Successfully migrated ${data.migratedCount} items to account`);
      }
      if (data.skippedCount > 0) {
        console.log(`[WishlistMigration] Skipped ${data.skippedCount} duplicate items`);
      }
      // Clear the visitor ID cookie after successful migration
      clearVisitorId();
      // Invalidate wishlist queries to refresh the UI
      utils.wishlist.invalidate();
    },
    onError: (error) => {
      console.error('[WishlistMigration] Migration failed:', error);
    },
  });

  useEffect(() => {
    // Wait for Clerk to load
    if (!isLoaded) return;

    // Only run when user is signed in and we haven't already migrated this session
    if (isSignedIn && !hasMigrated.current) {
      const visitorId = getVisitorIdFromCookie();

      if (visitorId) {
        hasMigrated.current = true;
        console.log('[WishlistMigration] User signed in, initiating wishlist migration');
        migration.mutate({ visitorId });
      }
    }
  }, [isSignedIn, isLoaded, migration]);

  // This component doesn't render anything
  return null;
}
